import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import KPIFilterDropdown, { type KPIFilterDropdownItem } from '../../../../AnalyticsHub/components/KPIFilterDropdown';
import { FontFamily } from '../../../../../constants/fonts';
import type { WesFilterKey, WesFilters } from '../useWesPalletStagingFilters';
import { WES_QUICK_SELECTS } from '../wesPalletStagingStaticData';
import WesPressable from './WesPressable';

interface FilterDef {
  key: WesFilterKey;
  label: string;
  group: 'geography' | 'timeframe';
}

const FILTER_DEFS: FilterDef[] = [
  { key: 'regions', label: 'REGION / UNIT', group: 'geography' },
  { key: 'markets', label: 'MARKET / POD', group: 'geography' },
  { key: 'locations', label: 'LOCATION', group: 'geography' },
  { key: 'years', label: 'YEAR', group: 'timeframe' },
  { key: 'periods', label: 'PERIOD', group: 'timeframe' },
  { key: 'weeks', label: 'WEEK IN YEAR', group: 'timeframe' },
];

const GROUPS: { key: FilterDef['group']; label: string }[] = [
  { key: 'geography', label: 'Geography' },
  { key: 'timeframe', label: 'Timeframe' },
];

interface WesFilterFieldsProps {
  filters: WesFilters;
  /** Mobile sheet: groups and filters stack vertically. */
  stacked?: boolean;
}

// The six filters in order, grouped Geography then Timeframe (story 353281). Edits go to the draft; nothing applies until Apply.
export const WesFilterFields: React.FC<WesFilterFieldsProps> = ({ filters, stacked = false }) => {
  const [openKey, setOpenKey] = useState<WesFilterKey | null>(null);
  const { draft, options, disabled, changeFilter } = filters;

  const selectedItems = useMemo(() => {
    const map = {} as Record<WesFilterKey, KPIFilterDropdownItem[]>;
    FILTER_DEFS.forEach(({ key }) => {
      map[key] = draft[key].map((id) => ({
        id,
        displayText: String(options[key].find((o) => String(o.id) === id)?.displayText ?? id),
      }));
    });
    return map;
  }, [draft, options]);

  const disabledIds = (key: WesFilterKey) => (key === 'periods' ? disabled.periods : key === 'weeks' ? disabled.weeks : undefined);

  return (
    <View style={[styles.groups, stacked && styles.groupsStacked]}>
      {GROUPS.map((group, index) => {
        // Filters in the group that have a selection.
        const selectedCount = FILTER_DEFS.filter((def) => def.group === group.key && draft[def.key].length > 0).length;
        return (
          <React.Fragment key={group.key}>
            {index > 0 ? <View style={[styles.partition, stacked && styles.partitionStacked]} testID="wes-filter-partition" /> : null}
            <View style={[styles.group, stacked && styles.groupStacked]} testID={`wes-filter-group-${group.key}`}>
              <View style={styles.groupHeadRow}>
                <Text style={styles.groupHead}>{group.label}</Text>
                {selectedCount > 0 ? (
                  <View style={styles.groupBadge} testID={`wes-filter-count-${group.key}`}>
                    <Text style={styles.groupBadgeText}>{selectedCount}</Text>
                  </View>
                ) : null}
              </View>
              <View style={[styles.filtersRow, stacked && styles.filtersRowStacked]}>
                {FILTER_DEFS.filter((def) => def.group === group.key).map((def) => (
                  <View key={def.key} style={stacked ? styles.filterItemStacked : styles.filterItem} testID={`wes-filter-item-${def.key}`}>
                    <Text style={styles.filterLabel} testID={`wes-filter-label-${def.key}`}>{def.label}</Text>
                    <KPIFilterDropdown
                      childList={options[def.key]}
                      selectedValue={selectedItems[def.key]}
                      setSelectedValue={(value) => changeFilter(def.key, value.map((v) => String(v.id)))}
                      placeholder="All"
                      disabledIds={disabledIds(def.key)}
                      isOpen={openKey === def.key}
                      onOpenChange={(open) => setOpenKey(open ? def.key : null)}
                      strictSelectionLabels
                      highlightSelected
                      testID={`wes-filter-${def.key}`}
                    />
                  </View>
                ))}
              </View>
            </View>
          </React.Fragment>
        );
      })}
    </View>
  );
};

interface WesQuickSelectsProps {
  filters: WesFilters;
  /** Mobile: one row that scrolls sideways. */
  scrollable?: boolean;
}

export const WesQuickSelects: React.FC<WesQuickSelectsProps> = ({ filters, scrollable = false }) => {
  const { activeQuickSelect, pressQuickSelect } = filters;
  const pills = WES_QUICK_SELECTS.map((option) => {
    const isActive = activeQuickSelect === option.key;
    return (
      <WesPressable
        key={option.key}
        style={[styles.pill, isActive && styles.pillActive]}
        hoverStyle={isActive ? undefined : styles.pillHover}
        onPress={() => pressQuickSelect(option.key)}
        accessibilityState={{ selected: isActive }}
        testID={`wes-quick-${option.key}`}
      >
        <Text style={[styles.pillText, isActive && styles.pillTextActive]} numberOfLines={1}>{option.label}</Text>
      </WesPressable>
    );
  });

  if (scrollable) {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.quickPills}
        testID="wes-quick-selects-scroll"
      >
        {pills}
      </ScrollView>
    );
  }

  return (
    <View style={styles.quickRow} testID="wes-quick-selects">
      <Text style={styles.quickLabel}>TIME FRAME:</Text>
      <View style={[styles.quickPills, styles.quickPillsWrap]}>{pills}</View>
    </View>
  );
};

interface WesFilterBarProps {
  filters: WesFilters;
}

// Desktop bar, directly below the tab navigation. Reset and Apply are always visible; Apply is disabled until the draft differs from the applied state.
const WesFilterBar: React.FC<WesFilterBarProps> = ({ filters }) => {
  const { isDirty, apply, reset } = filters;
  const [expanded, setExpanded] = useState(true);

  return (
    <View style={styles.wrapper}>
      <View style={styles.card} testID="wes-filter-bar">
        <View style={styles.headerRow}>
          <WesPressable
            style={styles.toggle}
            onPress={() => setExpanded((prev) => !prev)}
            accessibilityRole="button"
            accessibilityLabel="Filters"
            accessibilityState={{ expanded }}
            testID="wes-filter-toggle"
          >
            <View style={expanded ? undefined : styles.chevronCollapsed} testID="wes-filter-chevron">
              <Svg width={16} height={16} viewBox="0 0 24 24">
                <Path d="m6 9 6 6 6-6" fill="none" stroke="#616161" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
              </Svg>
            </View>
            <Svg width={16} height={16} viewBox="0 0 24 24" testID="wes-filter-icon">
              <Path d="M3 4h18l-7 8.5V19l-4 2v-8.5z" fill="none" stroke="#034895" strokeWidth={2} strokeLinejoin="round" />
            </Svg>
            <Text style={styles.toggleText}>Filters</Text>
          </WesPressable>
          <View style={styles.actions}>
            <WesPressable style={styles.resetBtn} hoverStyle={styles.resetHover} onPress={reset} testID="wes-filter-reset">
              <Text style={styles.resetText}>Reset</Text>
            </WesPressable>
            <WesPressable
              style={[styles.applyBtn, isDirty ? styles.applyEnabled : styles.applyDisabled]}
              hoverStyle={styles.applyHover}
              onPress={apply}
              disabled={!isDirty}
              accessibilityState={{ disabled: !isDirty }}
              testID="wes-filter-apply"
            >
              <Text style={[styles.applyText, isDirty && styles.applyTextEnabled]}>Apply</Text>
            </WesPressable>
          </View>
        </View>

        {expanded ? (
          <>
            <View style={styles.fieldsRow}>
              <WesFilterFields filters={filters} />
            </View>

            <View style={styles.divider} />

            <WesQuickSelects filters={filters} />
          </>
        ) : null}
      </View>
    </View>
  );
};

// Style values copied from CrewingAnalyticsDashboard (filterBar, filterItem, filterLabel, resetBtn, applyBtn, quickSelectPill).
const styles = StyleSheet.create({
  wrapper: { paddingHorizontal: 20, paddingTop: 4, paddingBottom: 16 },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 16,
    shadowColor: '#005EC0',
    shadowOpacity: 0.08,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 2,
  },
  row: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', gap: 10 },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 },
  toggle: { flexDirection: 'row', alignItems: 'center', columnGap: 8, paddingVertical: 4, paddingHorizontal: 2 },
  chevronCollapsed: { transform: [{ rotate: '-90deg' }] },
  toggleText: { fontSize: 14, fontWeight: '700', fontFamily: FontFamily.bold, color: '#2D2D2D' },
  fieldsRow: { marginTop: 10 },
  groupHeadRow: { flexDirection: 'row', alignItems: 'center', columnGap: 6, marginBottom: 8 },
  groupBadge: { minWidth: 18, height: 18, paddingHorizontal: 5, borderRadius: 9, backgroundColor: '#005CBC', alignItems: 'center', justifyContent: 'center' },
  groupBadgeText: { fontSize: 10.5, fontWeight: '700', fontFamily: FontFamily.bold, color: '#FFFFFF' },
  partition: { width: 1, alignSelf: 'stretch', backgroundColor: '#E6E6E6' },
  partitionStacked: { display: 'none' },
  groups: { flexDirection: 'row', flexWrap: 'wrap', flex: 1, columnGap: 14, rowGap: 14 },
  groupsStacked: { flexDirection: 'column', flex: 0, gap: 16 },
  group: { flexGrow: 1, minWidth: 0 },
  groupStacked: { flexGrow: 0 },
  groupHead: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#034895',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  filtersRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', gap: 10 },
  filtersRowStacked: { flexDirection: 'column', flexWrap: 'nowrap' },
  filterItem: { minWidth: 150, flexGrow: 1 },
  filterItemStacked: { width: '100%', minWidth: '100%', maxWidth: '100%' },
  filterLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#616161',
    fontFamily: FontFamily.bold,
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  actions: { flexDirection: 'row', alignItems: 'center', gap: 10, marginLeft: 'auto' },
  resetBtn: {
    borderWidth: 1,
    borderColor: '#034895',
    paddingHorizontal: 20,
    borderRadius: 999,
    backgroundColor: '#FFFFFF',
    height: 34,
    justifyContent: 'center',
  },
  resetHover: { backgroundColor: '#EFF5FC' },
  resetText: { fontSize: 14, fontFamily: FontFamily.bold, fontWeight: '700', color: '#034895' },
  applyBtn: { paddingHorizontal: 20, borderRadius: 999, height: 34, justifyContent: 'center' },
  applyEnabled: { backgroundColor: '#034895' },
  applyHover: { backgroundColor: '#0061C7' },
  applyDisabled: { backgroundColor: '#D0D0D0' },
  applyText: { fontSize: 14, fontFamily: FontFamily.bold, fontWeight: '700', color: '#616161' },
  applyTextEnabled: { color: '#FFFFFF' },
  divider: { height: 1, backgroundColor: '#E3E9F3', marginTop: 12, marginBottom: 12 },
  quickRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  quickLabel: { fontSize: 12, fontWeight: '700', fontFamily: FontFamily.bold, color: '#616161', textTransform: 'uppercase' },
  quickPills: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  quickPillsWrap: { flexWrap: 'wrap', flex: 1 },
  pill: {
    borderWidth: 1,
    borderColor: '#034895',
    borderRadius: 999,
    paddingHorizontal: 12,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  pillActive: { backgroundColor: '#005CBC', borderColor: '#005CBC' },
  pillHover: { backgroundColor: '#F3F7FD' },
  pillText: { fontSize: 11.5, fontWeight: '700', fontFamily: FontFamily.bold, color: '#034895' },
  pillTextActive: { color: '#FFFFFF', fontFamily: FontFamily.bold },
});

export default WesFilterBar;
